<template lang="pug">
v-dialog(v-model='visible' width='500px')
    template(v-slot:default='{ isActive }')
        v-card
            v-card-title.bg-red Are you sure?
            v-card-text.pa-4
                | Are you sure you would like to delete
                span.bg-red.font-weight-bold.ma-1 {{ itemNameWithDefault }}
                | ?
                span.ml-1 {{ permanentActionMessage }}
                span.font-weight-bold(v-if='deleteNotice') {{ deleteNotice }}
                v-card-actions
                    v-spacer
                    ButtonExit(@click='isActive.value = false') Cancel
                    ButtonDanger(@click='confirmed') Delete

    template(v-slot:activator='{ props: activatorProps }')
        IconButton(v-bind='activatorProps' icon='mdi-delete' color='red' :disabled='disabled')
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IconButton } from '@/common/';
import ButtonDanger from '@/common/button-components/ButtonDanger.vue';
import ButtonExit from '@/common/button-components/ButtonExit.vue';
import { alertException } from '@/util/errors';

export default defineComponent({
    name: 'DeleteConfirmationDialog',
    components: {
        ButtonExit,
        ButtonDanger,
        IconButton,
    },
    props: {
        item: {
            // The item to be deleted by deleteAction
            type: Object,
            required: true,
        },
        itemName: {
            // If this is `null` then `item.name` will be used as the itemName
            type: String,
            default: null,
        },
        deleteAction: {
            // is called with item when delete is confirmed
            type: Function,
            required: true,
        },
        additionalInfoToDisplayOnDeleteFailure: { type: String, default: '' },
        dataCyDelete: { type: String, default: undefined },
        dataCyConfirm: { type: String, default: undefined },
        dataCyCancel: { type: String, default: undefined },
        disabled: {
            type: Boolean,
            default: false,
        },
        permanentActionMessage: {
            type: String,
            default: 'This action cannot be undone',
        },
        deleteNotice: {
            type: String,
            default: undefined,
        },
    },
    data: function () {
        return {
            visible: false,
        };
    },
    computed: {
        itemNameWithDefault(): string {
            if (this.itemName) {
                return this.itemName;
            }
            if (this.item.name) {
                return this.item.name;
            }

            console.error(
                `itemName is ${this.itemName} and item.name is ${this.item.name}.  ` +
                    `Expected at least one to be a non-empty string.`,
            );
            return 'this item'; // Fallback
        },
    },
    watch: {
        deleteAction: {
            immediate: true,
            handler: function () {
                if (typeof this.deleteAction !== 'function') {
                    console.error(`: this.deleteAction is: `, this.deleteAction);
                    console.error(`: typeof this.deleteAction is: `, typeof this.deleteAction);
                    console.error('Expected this.deleteAction to be of type function');
                    console.error(
                        'Generally you want the prop. inside the DeleteConfirmationDialog to look something like:',
                    );
                    console.error(":delete-action='() => deleteAssetType(item)'");
                    throw new Error(
                        `deleteAction must be a function, but is of type: ${typeof this
                            .deleteAction}`,
                    );
                }
            },
        },
    },
    methods: {
        async confirmed(): Promise<void> {
            try {
                await this.deleteAction(this.item);
            } catch (e) {
                alertException(
                    e,
                    `Error while deleting ${this.itemNameWithDefault}.  ${this.additionalInfoToDisplayOnDeleteFailure}`,
                );
            } finally {
                this.visible = false;
            }
        },
    },
});
</script>
